import React from "react";
import { MessageBar, MessageBarButton, MessageBarType, TooltipHost } from "@fluentui/react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../src/taskpane/components/_v2/redux/storeMain";
import { cancelContentManagerMoveTo, setContentManagerMoveTo } from "../../src/taskpane/redux/modules/contentManagerDirectory";
import { LanguageContext } from "../../utilities/LocalizationModule";
import { CopyMoveTrayBlocker, resolveCopyMoveTrayState } from "./helpers/copyMoveTrayState";
import { resolveCopyMoveErrorMessage } from "./helpers/copyMoveFailure";

/**
 * Persistent banner for a copy/move that is waiting for a destination.
 *
 * Mounted once, above every content-manager body view, because the operation outlives
 * all of them: it is armed in one library (or in the all-documents grid), survives
 * navigation through the locations list and Home, and is committed in another library.
 * Previously it was only visible inside a document library, where the command bar
 * silently collapsed to Confirm/Cancel — so the aggregate flow, which hands the user
 * straight to the locations list, showed no trace of it at all.
 *
 * The commit button is here rather than only in the command bar so the reason it is
 * unavailable can be stated. See resolveCopyMoveTrayState for the gating rules.
 */
export const CopyMoveTray = () => {
    const languageStrings = React.useContext(LanguageContext);
    const dispatch = useDispatch();

    const moveTo = useSelector((state: RootState) => state.contentManagerDirectory.moveTo);
    const currentDirectory = useSelector((state: RootState) => state.workingDirectories.workingDirectories.at(-1));
    const currentSharepointFolder = useSelector((state: RootState) => state.currentSharepointFolder);
    // The body this tray sits above swaps the whole view for a spinner while loading,
    // taking the confirm dialog's host with it. See rule 3 in copyMoveTrayState.
    const isContentLoading = useSelector((state: RootState) => state.contentManagerDirectory.isContentLoading);

    const tray = React.useMemo(
        () => resolveCopyMoveTrayState({ moveTo, currentDirectory, currentSharepointFolder, isContentLoading }),
        [moveTo, currentDirectory, currentSharepointFolder, isContentLoading]
    );

    if (!tray.visible) {
        return null;
    }

    const countText = tray.isAllItems
        ? `${languageStrings.All} ${languageStrings.ItemsSelected}`
        : `${tray.itemCount} ${tray.itemCount === 1
            ? (tray.isMoveOperation ? languageStrings.ItemToMove : languageStrings.ItemToCopy).toLowerCase()
            : (tray.isMoveOperation ? languageStrings.ItemsToMove : languageStrings.ItemsToCopy).toLowerCase()}`;

    const blockerText = (blocker: CopyMoveTrayBlocker): string => {
        switch (blocker) {
            case "noPermission":
                return languageStrings.WrongUserPermissionsSave;
            case "alreadyAtDestination":
                return languageStrings.MultipleFilesFoldersCopyMove;
            case "destinationLoading":
            case "permissionPending":
                return languageStrings.Loading;
            // "noDestination", "unsupportedDestination" and "sameAsSource" all resolve to
            // the same instruction: go somewhere that can receive these items.
            default:
                return languageStrings.SelectDestinationDirectory;
        }
    };

    const message = tray.sourceLabel
        ? `${countText} (${tray.sourceLabel})`
        : countText;

    // A failed attempt leaves the operation armed and retryable, so this banner is still
    // on screen — and it is the only thing on screen that is. Without this the failure's
    // sole trace was a small command-bar chip reading "Something went wrong", whose real
    // reason appeared only if the user thought to click it open. Note the asymmetry it
    // used to sit behind: a THROWN error opens a modal, a returned `success: false` did
    // not.
    const errorText = resolveCopyMoveErrorMessage(moveTo?.error, languageStrings);

    return (
        <div style={{ paddingTop: "5px", paddingRight: "5px" }}>
            <MessageBar
                messageBarType={errorText ? MessageBarType.error : MessageBarType.warning}
                isMultiline={false}
                dismissButtonAriaLabel="Close"
                actions={
                    <div>
                        <TooltipHost content={tray.blocker ? blockerText(tray.blocker) : undefined}>
                            {/*
                              The span is load-bearing: Fluent gives a disabled button
                              `pointer-events: none`, so it never raises the hover the
                              tooltip listens for. Events fall through to this wrapper
                              instead, which is the whole reason the reason is readable
                              precisely when the button is unavailable.
                            */}
                            <span style={{ display: "inline-block" }}>
                                <MessageBarButton
                                    primary
                                    disabled={!tray.canCommit}
                                    // One-shot handoff: the confirm dialog lives in the library
                                    // view, which is guaranteed mounted whenever canCommit is true.
                                    onClick={() => dispatch(setContentManagerMoveTo({ commitRequested: true }))}
                                >
                                    {`${languageStrings.Confirm} ${tray.isMoveOperation ? languageStrings.MoveTo : languageStrings.SaveTo}`}
                                </MessageBarButton>
                            </span>
                        </TooltipHost>
                        <MessageBarButton onClick={() => dispatch(cancelContentManagerMoveTo())}>
                            {languageStrings.Cancel}
                        </MessageBarButton>
                    </div>
                }
            >
                {errorText
                    ? `${message} — ${errorText}`
                    : tray.blocker ? `${message} — ${blockerText(tray.blocker)}` : message}
            </MessageBar>
        </div>
    );
};
